import React, { useEffect, useState } from 'react';
import { Button, CircularProgress, Grid, MenuItem, Typography, useTheme } from '@mui/material';
import SpectrumTextField from '../../components/Spectrum/TextField/SpectrumTextField';
import SpectrumSelectField from '../../components/Spectrum/Select/SpectrumSelect';
import { cardMonths, cardYears, getCardBrand } from '../../utils/paymentUtils';
import PaymentBillingAddress from './PaymentBillingAddress';
import { isValidCreditCardNumber, isValidCVV, isZipValid } from '../../utils/validation';
import { doEncryption } from '../../scripts/PaymentScripts';
import {
  INewPaymentCardDetails,
  INewPaymentCardDetailsPayload,
  INewPaymentCardErrorDetails,
} from '../../types';

interface ICardPaymentProps {
  emitHeight: () => void;
  isMobileView: boolean;
  handleAddPaymentMethod: (data: INewPaymentCardDetailsPayload) => void;
  addPaymentLoading: boolean;
  addPaymentError: string;
}

const CardPayment = ({
  isMobileView,
  emitHeight,
  handleAddPaymentMethod,
  addPaymentError,
  addPaymentLoading,
}: ICardPaymentProps) => {
  const theme = useTheme();
  const cardYearsData = cardYears();
  const [cardDetails, setCardDetails] = useState<INewPaymentCardDetails>({
    customerState: '',
    expiryYear: '',
    expiryMonth: '',
  } as INewPaymentCardDetails);
  const [errorDetails, setErrorDetails] = useState<INewPaymentCardErrorDetails>(
    {} as INewPaymentCardErrorDetails,
  );
  const [encryptionError, setEncryptionError] = useState('');

  const validateFields = (
    {
      cardNumber,
      expiryMonth,
      expiryYear,
      cvv,
      customerName,
      customerAddress1,
      customerCity,
      customerZIP,
      customerState,
    }: INewPaymentCardDetails,
    errorDetails: INewPaymentCardErrorDetails,
  ) => {
    let valid = true;
    const errors = { ...errorDetails };
    if (!cardNumber) {
      valid = false;
      errors.cardNumber = 'Please enter card number';
    }
    if (cardNumber && !isValidCreditCardNumber(cardNumber)) {
      valid = false;
      errors.cardNumber = 'Please enter valid credit card number';
    }
    if (!expiryMonth) {
      valid = false;
      errors.expiryMonth = 'Please select month';
    }
    if (!expiryYear) {
      valid = false;
      errors.expiryYear = 'Please select year';
    }
    if (!cvv) {
      valid = false;
      errors.cvv = 'Please enter cvv';
    }
    if (cvv && !isValidCVV(cvv)) {
      valid = false;
      errors.cvv = 'Please enter valid cvv';
    }
    if (!customerName) {
      valid = false;
      errors.customerName = 'Please enter name';
    }
    if (!customerAddress1) {
      valid = false;
      errors.customerAddress1 = 'Please enter address line 1';
    }
    if (!customerZIP) {
      valid = false;
      errors.customerZIP = 'Please enter zip code';
    }
    if (customerZIP && !isZipValid(customerZIP)) {
      valid = false;
      errors.customerZIP = 'Please enter valid zip code';
    }
    if (!customerState) {
      valid = false;
      errors.customerState = 'Please select state';
    }
    if (!customerCity) {
      valid = false;
      errors.customerCity = 'Please enter city';
    }
    return { valid, errors };
  };
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name;
    let value: string | boolean = event.target.value;
    if (name === 'isDefault' || name === 'saveToWallet') {
      value = event.target.checked;
    }
    if (name === 'cardNumber' || name === 'cvv' || name === 'cardholderZip') {
      value = String(value).replace(/ /g, '');
    }
    setErrorDetails({ ...errorDetails, [name]: '' });
    setCardDetails({ ...cardDetails, [name]: value });
  };
  const handleContinue = () => {
    // Validating for required fields and custom validators
    const { valid, errors } = validateFields(cardDetails, errorDetails);
    if (!valid) {
      setErrorDetails(errors);
      return;
    }
    // Encrypting credit card numbers
    const {
      isValid,
      errors: encryptionError,
      result,
      phase,
      keyId,
    } = doEncryption(cardDetails?.cardNumber, cardDetails?.cvv);
    if (isValid) {
      const {
        cardNumber,
        customerName,
        customerAddress1,
        customerAddress2,
        customerCity,
        customerState,
        customerZIP,
      } = cardDetails;
      const cardBrand = getCardBrand(cardNumber);
      const [encryptedCreditCardNumber, encryptedCardSecurityValue, encryptedIntegrityCheck] =
        result;
      // request payload
      const payload: INewPaymentCardDetailsPayload = {
        customerName,
        customerAddress1,
        customerAddress2: customerAddress2 || '',
        customerCity,
        customerState,
        customerZIP,
        paymentMethodSuffix: cardNumber.slice(cardNumber.length - 4),
        cardBrand,
        expiration: cardDetails?.expiryMonth + '' + cardDetails?.expiryYear,
        encryptedCreditCardNumber,
        encryptedIntegrityCheck,
        encryptedCardSecurityValue,
        CustomerPhone: '',
        CustomerCountryCode: '',
        PhaseId: phase,
        KeyId: keyId,
      };
      handleAddPaymentMethod(payload);
    } else {
      setEncryptionError(encryptionError);
    }
  };
  const error = addPaymentError || encryptionError;

  useEffect(() => {
    emitHeight();
  }, [JSON.stringify(errorDetails), error]);

  return (
    <div>
      <Typography variant='h4' sx={{ mt: 8, mb: 10 }}>
        Card Details
      </Typography>
      <Grid container spacing={8}>
        <Grid item width='100%'>
          <SpectrumTextField
            required
            label='Card Number'
            variant='outlined'
            size='small'
            name='cardNumber'
            onChange={handleChange}
            value={cardDetails?.cardNumber}
            error={!!errorDetails?.cardNumber}
            helperText={errorDetails?.cardNumber}
          />
        </Grid>
        <Grid item small={isMobileView ? 12 : 4} width='100%'>
          <SpectrumSelectField
            required
            label='Month'
            variant='outlined'
            size='small'
            name='expiryMonth'
            onChange={handleChange}
            value={cardDetails?.expiryMonth}
            error={!!errorDetails?.expiryMonth}
            helperText={errorDetails?.expiryMonth}
          >
            {cardMonths.map((item) => (
              <MenuItem key={item} value={item}>
                {item}
              </MenuItem>
            ))}
          </SpectrumSelectField>
        </Grid>
        <Grid item small={isMobileView ? 12 : 4} width='100%'>
          <SpectrumSelectField
            required
            label='Year'
            variant='outlined'
            size='small'
            name='expiryYear'
            onChange={handleChange}
            value={cardDetails?.expiryYear}
            error={!!errorDetails?.expiryYear}
            helperText={errorDetails?.expiryYear}
          >
            {cardYearsData.map((item) => (
              <MenuItem key={item?.value} value={item?.value}>
                {item?.label}
              </MenuItem>
            ))}
          </SpectrumSelectField>
        </Grid>
        <Grid item small={isMobileView ? 12 : 4} width='100%'>
          <SpectrumTextField
            required
            label='CVV'
            variant='outlined'
            size='small'
            name='cvv'
            onChange={handleChange}
            value={cardDetails?.cvv}
            error={!!errorDetails?.cvv}
            helperText={errorDetails?.cvv}
          />
        </Grid>
      </Grid>
      <PaymentBillingAddress
        handleChange={handleChange}
        details={cardDetails}
        errorDetails={errorDetails}
        isMobileView={isMobileView}
      />
      {error && (
        <Typography textAlign='center' mt={10} mb={10} variant='paragraph2' color='error'>
          {error}
        </Typography>
      )}
      <Button
        sx={{ mt: 12, mb: 4 }}
        variant='contained'
        size='large'
        fullWidth
        onClick={handleContinue}
      >
        {addPaymentLoading ? (
          <CircularProgress style={{ color: theme.palette.common.white }} />
        ) : (
          'Continue'
        )}
      </Button>
    </div>
  );
};
export default CardPayment;
